import { useState, type ReactNode } from 'react';
import { Avatar, Button, Dropdown, Layout, Menu, Space, Typography, type MenuProps } from 'antd';
import {
  AppstoreOutlined, BankOutlined, BarChartOutlined, DownOutlined, FileSearchOutlined, GlobalOutlined,
  HomeOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, SafetyCertificateOutlined,
  TeamOutlined, UserSwitchOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const { Header, Sider, Content } = Layout;

export const AppLayout = ({ children }: { children: ReactNode }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const item = (label: string, key: string, icon: ReactNode, permission: string): NonNullable<MenuProps['items']>[number] | null =>
    hasPermission(permission) ? { label, key, icon } : null;
  const items = [
    item('Dashboard', '/', <HomeOutlined />, 'dashboard.view'),
    item('Cases', '/cases', <FileSearchOutlined />, 'cases.view'),
    item('My Assigned Cases', '/my-cases', <UserSwitchOutlined />, 'cases.view'),
    item('Reports', '/reports', <BarChartOutlined />, 'reports.view'),
    (hasPermission('insurance.view') || hasPermission('hospitals.view') || hasPermission('regions.view')) ? {
      label: 'Masters', key: 'masters', icon: <AppstoreOutlined />, children: [
        item('Insurance Agencies', '/masters/insurance', <SafetyCertificateOutlined />, 'insurance.view'),
        item('Hospitals', '/masters/hospitals', <BankOutlined />, 'hospitals.view'),
        item('Regions', '/masters/regions', <GlobalOutlined />, 'regions.view')
      ].filter(Boolean)
    } : null,
    (hasPermission('users.view') || hasPermission('roles.view')) ? {
      label: 'Settings', key: 'settings', icon: <SafetyCertificateOutlined />, children: [
        item('Users', '/settings/users', <TeamOutlined />, 'users.view'),
        item('Roles', '/settings/roles', <SafetyCertificateOutlined />, 'roles.view'),
        item('Roles & Permissions', '/settings/permissions', <SafetyCertificateOutlined />, 'roles.view')
      ].filter(Boolean)
    } : null
  ].filter(Boolean) as MenuProps['items'];
  const userMenu: MenuProps = { items: [{ key: 'logout', icon: <LogoutOutlined />, label: 'Sign out', onClick: () => { logout(); navigate('/login'); } }] };

  return (
    <Layout className="app-shell">
      <Sider width={256} collapsed={collapsed} trigger={null} className="sidebar">
        <div className="brand" onClick={() => navigate('/')}>
          <img src={collapsed ? '/favicon.png' : '/assets/eagleeye-logo.png'} alt="EagleEye Investigations" />
        </div>
        <Menu theme="dark" mode="inline" items={items} selectedKeys={[location.pathname]} onClick={({ key }) => key.startsWith('/') && navigate(key)} />
      </Sider>
      <Layout>
        <Header className="topbar">
          <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed(!collapsed)} />
          <Dropdown menu={userMenu} placement="bottomRight">
            <Space className="user-menu">
              <Avatar>{user?.name?.[0]?.toUpperCase()}</Avatar>
              <span className="user-copy"><Typography.Text strong>{user?.name}</Typography.Text><small>{user?.role?.name}</small></span>
              <DownOutlined />
            </Space>
          </Dropdown>
        </Header>
        <Content className="page-content">{children}</Content>
      </Layout>
    </Layout>
  );
};
